/**
 * @file Navbar.js
 * @description This file contains the implementation of the Navbar component.
 */
import { useContext, useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { StylingContext, useAuthContext } from '../../store/Contexts';
import { useTheme } from '../../store/Contexts';
import './Navbar.css';
import { SecurityAttributes, UserType } from '../../utils/data/enums';
import { isMobileDevice } from '../../utils/deviceCheck';
import MultiTenantMenu from './MultiTenantMenu';
import ClientMenu from './ClientMenu';
import PlaceholderLogo from '../ui/PlaceholderLogo';
import { ROUTES } from '../../constants/routes';
import { getText } from '../../i18n';

function Navbar({ styleOverride = {} }) {
    const {
        isLoggedIn,
        user,
        multitenantFlag,
        hasSecurityAttribute,
        adminView,
    } = useAuthContext();
    const { currentStyling } = useContext(StylingContext);
    const themeStyles = useTheme();
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

    const isLimitedUser = user?.jwt?.type === UserType.LimitedUser;
    const isImpersonating =
        window.localStorage.getItem('impersonation_token') !== null;

    useEffect(() => {
        let isPhysicalMobileDevice = false;

        const updateMobileState = () => {
            const isMobileViewport = window.innerWidth <= 768;
            setIsMobile(isPhysicalMobileDevice || isMobileViewport);
        };

        const checkMobileDevice = async () => {
            isPhysicalMobileDevice = await isMobileDevice();
            updateMobileState();
        };

        checkMobileDevice();

        window.addEventListener('resize', updateMobileState);

        return () => {
            window.removeEventListener('resize', updateMobileState);
        };
    }, []);

    const isAdmin = user?.jwt?.type <= UserType.ClientAdmin;
    const isNavBarVisible = isMobile ? isLoggedIn && adminView : isLoggedIn;

    return (
        isNavBarVisible && (
            <div className={`sidenav${currentStyling.navbarSidenavClass}`}>
                <div
                    className={`sidenavContent${currentStyling.navbarContentClass}`}
                >
                    <div>
                        {styleOverride?.secondaryLogo ||
                        themeStyles.secondaryLogo ? (
                            <img
                                className={`${currentStyling.navbarIMGClass} navbarIMGClass`}
                                src={
                                    styleOverride?.secondaryLogo ||
                                    themeStyles.secondaryLogo
                                }
                                alt={getText('msg_5807dd602664')}
                            />
                        ) : (
                            <PlaceholderLogo
                                className={`${currentStyling.navbarIMGClass} navbarIMGClass`}
                            />
                        )}
                    </div>
                    <div className={'sidenavMenu'}>
                        {!multitenantFlag &&
                            !(
                                isLimitedUser &&
                                !hasSecurityAttribute([
                                    SecurityAttributes.UserAccessPortals,
                                ])
                            ) && (
                                <NavLink
                                    to={ROUTES.ROOT}
                                    className={`${currentStyling.navbarLinkClass}`}
                                    exact="true"
                                >
                                    <i
                                        className={`bi bi-house-door-fill${currentStyling.navbarIconClass}`}
                                    />
                                    <div
                                        className={`text${currentStyling.navbarTextClass}`}
                                    >
                                        {getText('msg_70f8bb9a8a53')}
                                    </div>
                                </NavLink>
                            )}

                        {multitenantFlag && <MultiTenantMenu />}

                        {!multitenantFlag && <ClientMenu />}
                    </div>
                </div>
            </div>
        )
    );
}

export default Navbar;
